import * as React from 'react';
import { FixMeLater } from '../../utils';
import { useStyles } from './FlexGrid.styles';
import { Styler, StylerBaseProps } from '../Styler';
import { FlexGridContext, FlexGridContextValues, useFlexGrid } from './FlexGrid.context';
import { DEFAULT_GRID_COLUMNS, FlexGridItemSize } from '../../theming/types/components/FlexGrid.types';
import { ResponsiveStyleValue, SpacingValue } from '../../theming';

export interface FlexGridProps extends StylerBaseProps {
    /**
     * If `true`, the component will behave as a grid container.
     * Flex grid items must have a container as their immediate parent.
     */
    container?: boolean;

    /**
     * No longer required but handled for backward compatibility.
     * @ignore
     */
    item?: boolean;

    /**
     * If `true`, the grid container will fill the height of its parent container.
     */
    fullHeight?: boolean;

    /**
     * The number of colums in the grid. Only relevant when `container` is `true`.
     * @default 12
     */
    columns?: number;

    /**
     * Sets the spacing that will applied between child grid items. Only relevant when `container` is `true`.
     * If a number, the value is in pixels and will be multiplied by the theme spacing unit.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 0
     */
    spacing?: ResponsiveStyleValue<SpacingValue>;

    /**
     * Sets the horizontal spacing that will applied between child grid items. Only relevant when `container` is `true`.
     * This will take precedence over the `spacing` prop,
     * If a number, the value is in pixels and will be multiplied by the theme spacing unit.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 0
     */
    columnSpacing?: ResponsiveStyleValue<SpacingValue>;

    /**
     * Sets the vertical spacing that will applied between child grid items. Only relevant when `container` is `true`.
     * This will take precedence over the `spacing` prop,
     * If a number, the value is in pixels and will be multiplied by the theme spacing unit.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 0
     */
    rowSpacing?: ResponsiveStyleValue<SpacingValue>;

    /**
     * If a number, sets the number of columns the grid item will occupy.
     * If `auto`, the grid item will occupy the space defined by its content.
     * If `fill`, the grid item will fill any remaining available space.
     * If `hold`, the grid item will occupy the space defined by its content and will not shrink.
     * If `equal`, the grid item will occupy space equally with any siblings sharing this prop value.
     * Applied to `xs` breakpoint and up if not overriden.
     */
    xs?: FlexGridItemSize;

    /**
     * If a number, sets the number of columns the grid item will occupy.
     * If `auto`, the grid item will occupy the space defined by its content.
     * If `fill`, the grid item will fill any remaining available space.
     * If `hold`, the grid item will occupy the space defined by its content and will not shrink.
     * If `equal`, the grid item will occupy space equally with any siblings sharing this prop value.
     * Applied to `s` breakpoint and up if not overriden.
     */
    s?: FlexGridItemSize;

    /**
     * If a number, sets the number of columns the grid item will occupy.
     * If `auto`, the grid item will occupy the space defined by its content.
     * If `fill`, the grid item will fill any remaining available space.
     * If `hold`, the grid item will occupy the space defined by its content and will not shrink.
     * If `equal`, the grid item will occupy space equally with any siblings sharing this prop value.
     * Applied to `m` breakpoint and up if not overriden.
     */
    m?: FlexGridItemSize;

    /**
     * If a number, sets the number of columns the grid item will occupy.
     * If `auto`, the grid item will occupy the space defined by its content.
     * If `fill`, the grid item will fill any remaining available space.
     * If `hold`, the grid item will occupy the space defined by its content and will not shrink.
     * If `equal`, the grid item will occupy space equally with any siblings sharing this prop value.
     * Applied to `l` breakpoint and up if not overriden.
     */
    l?: FlexGridItemSize;

    /**
     * If a number, sets the number of columns the grid item will occupy.
     * If `auto`, the grid item will occupy the space defined by its content.
     * If `fill`, the grid item will fill any remaining available space.
     * If `hold`, the grid item will occupy the space defined by its content and will not shrink.
     * If `equal`, the grid item will occupy space equally with any siblings sharing this prop value.
     * Applied to `xl` breakpoint and up if not overriden.
     */
    xl?: FlexGridItemSize;

    /**
     * @internal
     */
    parentLevel?: number | null;

    /**
     * If `true`, the negative margins will be removed from the bottom and right sides of the grid. This can be used to prevent overflow in small viewports.
     */
    disableEqualOverflow?: boolean;
}

/**
 * FlexGrid is a responsive layout grid based on CSS's Flexible Box module. It is used to arrange content into columns and rows
 * and ensure consitency across layouts. By default a grid will have 12 colums. Child FlexGrid components must be nested inside a parent
 * FlexGrid component with the container property set to true.
 */
export const FlexGrid: React.ForwardRefExoticComponent<FlexGridProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function FlexGridFn(props: FlexGridProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            children,
            container = false,
            columns: columnsProp = DEFAULT_GRID_COLUMNS,
            spacing: spacingProp,
            columnSpacing: columnSpacingProp,
            rowSpacing: rowSpacingProp,
            item: itemProp,
            fullHeight = false,
            alignContent,
            alignItems,
            flexDirection = 'row',
            flexWrap = 'wrap',
            justifyContent,
            xs,
            s,
            m,
            l,
            xl,
            parentLevel = null,
            disableEqualOverflow: disableEqualOverflowProp = false,
            ...other
        } = props;

        const localColumnSpacing = columnSpacingProp ?? spacingProp;
        const localRowSpacing = rowSpacingProp ?? spacingProp;

        // If this is a grid item, then we derive the spacing and columns from the parent container.
        const {
            columns: parentColumns,
            columnSpacing: parentColumnSpacing,
            rowSpacing: parentRowSpacing,
            disableEqualOverflow: parentDisableEqualOverflow,
        } = useFlexGrid();

        // This is a grid item unless we have no anscestor.
        const item = parentLevel !== null;

        // An item may also be a container.
        const isNestedContainer = item && container;

        // If this is a nested container then we increment the level by one.
        const level = isNestedContainer ? parentLevel + 1 : parentLevel ?? 0;

        const columns = container ? columnsProp : parentColumns;

        // If this is a container and the spacing prop has not been set then we will inherit spacing from the parent container.
        const columnSpacing = container ? localColumnSpacing ?? parentColumnSpacing : parentColumnSpacing;
        const rowSpacing = container ? localRowSpacing ?? parentRowSpacing : parentRowSpacing;

        const disableEqualOverflow = container ? disableEqualOverflowProp : parentDisableEqualOverflow;

        const { styles } = useStyles({
            columns,
            container,
            item,
            columnSpacing,
            rowSpacing,
            xs,
            s,
            m,
            l,
            xl,
            level,
            disableEqualOverflow,
        });

        // These flex props should only be applied to a container.
        const containerFlexProps = container
            ? {
                  alignContent,
                  alignItems,
                  flexDirection,
                  flexWrap,
                  justifyContent,
              }
            : {};

        const component = (
            <Styler
                ref={ref}
                baseStyles={styles.root}
                data-grid-container={container || undefined}
                data-grid-item={item || undefined}
                {...(fullHeight ? { h: '100%' } : {})}
                {...containerFlexProps}
                {...other}
            >
                {React.Children.map(children, (child) => {
                    if (React.isValidElement(child) && child.type === FlexGrid) {
                        return React.cloneElement(child, {
                            parentLevel: level,
                        } as FlexGridProps);
                    }
                    return child;
                })}
            </Styler>
        );

        const contextValue: FlexGridContextValues = React.useMemo(() => {
            return { columnSpacing, rowSpacing, columns, disableEqualOverflow };
        }, [columnSpacing, rowSpacing, columns, disableEqualOverflow]);

        if (container) {
            return <FlexGridContext.Provider value={contextValue}>{component}</FlexGridContext.Provider>;
        }

        return component;
    });
